import { useCallback, useState } from 'react';

import { AlertTriangle } from '@crehana/compass.icons.outline/v2/alert-triangle';
import { FollowUp } from '@crehana/compass.icons.outline/v2/follow-up';
import { Refresh } from '@crehana/compass.icons.outline/v2/refresh';
import { Button } from '@crehana/compass.ui/button';
import {
  ComplexDialogBody,
  ComplexDialogContent,
  ComplexDialogHeader,
  ComplexDialogIcon,
  ComplexDialogTitle,
} from '@crehana/compass.ui/modals/dialogs/complex-dialog';

import { useHomeTranslations } from '../../../../hooks/use-home-translations';
import type { PayrollReceipt } from '../../types';
import { getReceiptModalTitleParams } from '../../utils/format-payroll-date';
import { PdfViewer } from './pdf-viewer';

type ViewReceiptModalProps = {
  receipt: PayrollReceipt;
  onClose: () => void;
};

export function ViewReceiptModal({ receipt }: ViewReceiptModalProps) {
  const { t, languageCode } = useHomeTranslations();
  const [pdfStatus, setPdfStatus] = useState<'loading' | 'loaded' | 'error'>(
    'loading',
  );
  const [retryKey, setRetryKey] = useState(0);

  const titleParams = getReceiptModalTitleParams(
    receipt.startDate,
    receipt.endDate,
    receipt.periodNumber,
    languageCode,
  );

  const modalTitle = t('PAYROLL_MODAL_RECEIPT_TITLE_2', {
    interpolation: titleParams,
  });

  const handlePdfLoad = useCallback(() => {
    setPdfStatus('loaded');
  }, []);

  const handlePdfError = useCallback(() => {
    setPdfStatus('error');
  }, []);

  const handleRetry = useCallback(() => {
    setPdfStatus('loading');
    setRetryKey(k => k + 1);
  }, []);

  const renderContent = () => {
    if (pdfStatus === 'error') {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-[54rem]">
          <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-cds-gap-content-elements">
            <span className="tw-rounded-full tw-cds-w-56 tw-cds-h-56 tw-flex tw-items-center tw-justify-center tw-bg-[var(--cds-icon-bg-alert)]">
              <AlertTriangle
                className="tw-text-[var(--cds-icon-color-alert)]"
                size={24}
              />
            </span>

            <div>
              <p className="tw-cds-font-subtitle-01 tw-text-center tw-font-semibold">
                {t('PAYROLL_MODAL_ERROR_MESSAGE')}
              </p>
              <p className="tw-cds-font-body-01 tw-text-center tw-cds-mt-8">
                {t('PAYROLL_MODAL_ERROR_DESCRIPTION')}
              </p>
            </div>

            <Button type="button" size="sm" onClick={handleRetry}>
              <Refresh size={20} className="tw-text-inherit tw-fill-current" />
              {t('PAYROLL_MODAL_RETRY')}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <PdfViewer
        key={retryKey}
        pdfUrl={receipt.pdfUrl}
        onLoad={handlePdfLoad}
        onError={handlePdfError}
      />
    );
  };

  return (
    <ComplexDialogContent
      size="l"
      className="tw-flex tw-flex-col tw-max-h-[90vh]"
    >
      <ComplexDialogHeader>
        <ComplexDialogIcon icon={FollowUp} />
        <ComplexDialogTitle className="tw-flex tw-items-center tw-gap-space-02">
          <p>
            <span className="tw-cds-font-subtitle-03">
              {t('PAYROLL_MODAL_RECEIPT_TITLE_1')}
            </span>{' '}
            <span className="tw-cds-font-link-03 tw-text-[var(--cds-link-primary)] tw-font-semibold">
              {modalTitle}
            </span>
          </p>
        </ComplexDialogTitle>
      </ComplexDialogHeader>

      <ComplexDialogBody className="tw-flex tw-flex-col tw-gap-space-04 tw-min-h-0">
        {renderContent()}
      </ComplexDialogBody>
    </ComplexDialogContent>
  );
}
